import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../../context/ToastContext';
import { AuthService } from '../../../services/auth.service';
import { GroupConsolidationService } from '../../../services/intercompany.service';
import {
  ConsolidatedBalanceSheet, ConsolidatedIncomeStatement, ConsolidationLine, ConsolidationMember, UnreconciledLine,
} from '../../../interfaces/intercompany.interface';
import { toLocalIsoDate } from '../../../utils/dateUtils';
import { formatMoney } from '../../../utils/format';

type Mode = 'BS' | 'IS';
const CELL = { flex: '1 1 180px', maxWidth: 240 } as const;

const Section: React.FC<{ title: string; lines: ConsolidationLine[]; members: ConsolidationMember[]; bold?: boolean }> = ({ title, lines, members, bold }) => (
  <>
    {title && <TableRow><TableCell colSpan={members.length + 4}><Typography variant="subtitle2">{title}</Typography></TableCell></TableRow>}
    {lines.map(l => (
      <TableRow key={l.key} sx={bold ? { '& td': { fontWeight: 700 } } : undefined}>
        <TableCell sx={{ pl: title ? 3 : 2 }}>{l.code ? `${l.code} - ${l.label}` : l.label}</TableCell>
        {members.map(m => <TableCell key={m.institutionId} align="right">{formatMoney(l.byMember[m.institutionId] ?? 0)}</TableCell>)}
        <TableCell align="right">{formatMoney(l.total)}</TableCell>
        <TableCell align="right">{formatMoney(l.elimination)}</TableCell>
        <TableCell align="right">{formatMoney(l.consolidated)}</TableCell>
      </TableRow>
    ))}
  </>
);

const GroupConsolidation: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const [mode, setMode] = useState<Mode>('BS');
  const [asOf, setAsOf] = useState(toLocalIsoDate());
  const [from, setFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [to, setTo] = useState(toLocalIsoDate());
  const [loading, setLoading] = useState(false);
  const [bs, setBs] = useState<ConsolidatedBalanceSheet | null>(null);
  const [is, setIs] = useState<ConsolidatedIncomeStatement | null>(null);

  const run = async () => {
    try {
      setLoading(true);
      if (mode === 'BS') setBs(await GroupConsolidationService.balanceSheet(institutionId, asOf));
      else setIs(await GroupConsolidationService.incomeStatement(institutionId, from, to));
    } catch (e) {
      toast.showApiError(e, 'Failed to build the consolidation');
    } finally {
      setLoading(false);
    }
  };

  const exportAs = async (format: 'pdf' | 'xlsx') => {
    try {
      if (mode === 'BS') await GroupConsolidationService.exportBalanceSheet(institutionId, asOf, format);
      else await GroupConsolidationService.exportIncomeStatement(institutionId, from, to, format);
    } catch (e) {
      toast.showApiError(e, 'Failed to export');
    }
  };

  const members = (mode === 'BS' ? bs?.members : is?.members) ?? [];
  const unreconciled: UnreconciledLine[] = (mode === 'BS' ? bs?.unreconciled : is?.unreconciled) ?? [];
  const hasData = mode === 'BS' ? !!bs : !!is;

  return (
    <Box data-help-page="accounting.groupConsol.page" sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Group Consolidation</Typography>
      <Box data-help-section="accounting.groupConsol.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField data-help="accounting.groupConsol.statement" select size="small" label="Statement" value={mode} onChange={e => setMode(e.target.value as Mode)} sx={CELL}>
          <MenuItem value="BS">Balance sheet</MenuItem>
          <MenuItem value="IS">Income statement</MenuItem>
        </TextField>
        {mode === 'BS' ? (
          <TextField data-help="accounting.groupConsol.asOf" size="small" type="date" label="As of" value={asOf} onChange={e => setAsOf(e.target.value)} InputLabelProps={{ shrink: true }} sx={CELL} />
        ) : (
          <>
            <TextField data-help="accounting.groupConsol.from" size="small" type="date" label="From" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={CELL} />
            <TextField data-help="accounting.groupConsol.to" size="small" type="date" label="To" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={CELL} />
          </>
        )}
        <Button data-help="accounting.groupConsol.run" variant="contained" onClick={run} disabled={loading}>Run</Button>
        <Button data-help="accounting.groupConsol.pdf" onClick={() => exportAs('pdf')} disabled={!hasData}>PDF</Button>
        <Button data-help="accounting.groupConsol.xlsx" onClick={() => exportAs('xlsx')} disabled={!hasData}>XLSX</Button>
      </Box>
      {loading && <CircularProgress />}
      {!loading && hasData && (
        <>
          {mode === 'BS' && bs && !bs.balanced && <Alert severity="warning" sx={{ mb: 2 }}>The consolidated balance sheet does not balance.</Alert>}
          <TableContainer component={Paper}>
            <Table data-help-section="accounting.groupConsol.statementTable" size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="accounting.groupConsol.col.account">Account</TableCell>
                  {members.map(m => <TableCell key={m.institutionId} align="right">{m.name}</TableCell>)}
                  <TableCell data-help="accounting.groupConsol.col.total" align="right">Total</TableCell><TableCell data-help="accounting.groupConsol.col.elimination" align="right">IC elimination</TableCell><TableCell data-help="accounting.groupConsol.col.consolidated" align="right">Consolidated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mode === 'BS' && bs && (
                  <>
                    <Section title="Assets" lines={bs.assets} members={members} />
                    <Section title="Liabilities" lines={bs.liabilities} members={members} />
                    <Section title="Equity" lines={bs.equity} members={members} />
                    <Section title="" lines={bs.totals} members={members} bold />
                  </>
                )}
                {mode === 'IS' && is && (
                  <>
                    <Section title="Revenue" lines={is.revenue} members={members} />
                    <Section title="Expenses" lines={is.expenses} members={members} />
                    <Section title="" lines={is.totals} members={members} bold />
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>Unreconciled IC differences</Typography>
          {unreconciled.length === 0 ? <Typography variant="body2">None: every inter-company pair agrees.</Typography> : (
            <TableContainer component={Paper}>
              <Table data-help-section="accounting.groupConsol.unreconciled" size="small">
                <TableHead>
                  <TableRow><TableCell data-help="accounting.groupConsol.unrec.col.pair">Pair</TableCell><TableCell data-help="accounting.groupConsol.unrec.col.holder" align="right">Holder side</TableCell><TableCell data-help="accounting.groupConsol.unrec.col.counterparty" align="right">Counterparty side</TableCell><TableCell data-help="accounting.groupConsol.unrec.col.difference" align="right">Difference</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {unreconciled.map((u, i) => (
                    <TableRow key={i}>
                      <TableCell>{u.label}</TableCell>
                      <TableCell align="right">{formatMoney(u.holderAmount)}</TableCell>
                      <TableCell align="right">{formatMoney(u.counterpartyAmount)}</TableCell>
                      <TableCell align="right">{formatMoney(u.difference)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default GroupConsolidation;
